/**
 * Dashboard Ejecutivo de Reportes, Métricas y KPI (Rol Dueño).
 *
 * Muestra el resumen financiero por periodo (hoy, semana, mes), desglose por
 * método de pago, ranking top 10 productos más vendidos y tiempos de cocina KDS.
 */
import { useEffect, useState, useCallback } from 'react';
import { api } from '../lib/api';

interface ResumenVentas {
  periodo: string;
  totalOrdenes: number;
  totalVentas: number;
  desglosePagos: {
    efectivo: number;
    terminal: number;
    transferencia: number;
  };
}

interface TopProducto {
  productoNombre: string;
  unidadesVendidas: number;
  ingresoTotal: number;
}

interface RendimientoCocina {
  metaMinutos: number;
  ordenesProcesadas: number;
  promedioMinutos: number;
}

interface ComparativoSucursal {
  id: number;
  nombre: string;
  tipo: string;
  totalVentas: number;
  totalOrdenes: number;
}

export function Reportes({ sucursalId }: { sucursalId: number }) {
  const [periodo, setPeriodo] = useState<'hoy' | 'semana' | 'mes' | 'todo'>('hoy');

  const [resumen, setResumen] = useState<ResumenVentas | null>(null);
  const [topProductos, setTopProductos] = useState<TopProducto[]>([]);
  const [cocina, setCocina] = useState<RendimientoCocina | null>(null);
  const [sucursales, setSucursales] = useState<ComparativoSucursal[]>([]);

  const [cargando, setCargando] = useState(true);

  const cargarDatos = useCallback(async () => {
    try {
      setCargando(true);
      const [resVentas, resTop, resCocina, resSuc] = await Promise.all([
        api<ResumenVentas>(`/reportes/ventas?sucursalId=${sucursalId}&periodo=${periodo}`),
        api<TopProducto[]>(`/reportes/top-productos?sucursalId=${sucursalId}&limite=10`),
        api<RendimientoCocina>(`/reportes/cocina-rendimiento?sucursalId=${sucursalId}`),
        api<ComparativoSucursal[]>(`/reportes/sucursales`),
      ]);

      setResumen(resVentas);
      setTopProductos(resTop);
      setCocina(resCocina);
      setSucursales(resSuc);
    } catch (err) {
      console.error(err);
    } finally {
      setCargando(false);
    }
  }, [sucursalId, periodo]);

  useEffect(() => {
    cargarDatos();
  }, [cargarDatos]);

  if (cargando && !resumen) {
    return <p className="py-24 text-center text-salvia font-bold">Cargando reporte ejecutivo…</p>;
  }

  const promedioPorComanda =
    resumen && resumen.totalOrdenes > 0 ? resumen.totalVentas / resumen.totalOrdenes : 0;

  return (
    <div className="mx-auto max-w-5xl px-4 py-6 font-cuerpo text-tinta">
      {/* Header y Filtro de Periodo */}
      <header className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between border-b border-salvia/15 pb-4">
        <div>
          <span className="text-xs font-bold uppercase tracking-wider text-nopal-oscuro">Módulo Ejecutivo</span>
          <h1 className="font-titulo text-2xl font-extrabold text-tinta">Reportes y Métricas Globales</h1>
        </div>

        <div className="flex rounded-xl bg-cal p-1 border border-salvia/20">
          {(['hoy', 'semana', 'mes', 'todo'] as const).map((p) => (
            <button
              key={p}
              onClick={() => setPeriodo(p)}
              className={`rounded-lg px-3 py-1.5 text-xs font-bold capitalize transition ${
                periodo === p ? 'bg-nopal text-white shadow-sm' : 'text-salvia hover:text-tinta'
              }`}
            >
              {p === 'hoy' ? 'Hoy' : p === 'semana' ? 'Esta Semana' : p === 'mes' ? 'Este Mes' : 'Todo'}
            </button>
          ))}
        </div>
      </header>

      {/* KPI Cards principales */}
      {resumen && (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3 mb-8">
          <div className="rounded-3xl border border-nopal/30 bg-nopal-claro p-5 shadow-sm">
            <span className="text-xs font-bold uppercase text-nopal-oscuro block">Ingresos Totales</span>
            <div className="font-titulo text-3xl font-extrabold text-nopal-oscuro mt-1">
              ${resumen.totalVentas.toFixed(2)}
            </div>
            <span className="text-[11px] text-nopal-oscuro font-semibold mt-1 block">
              Periodo: {periodo}
            </span>
          </div>

          <div className="rounded-3xl border border-salvia/20 bg-white p-5 shadow-sm">
            <span className="text-xs font-bold uppercase text-salvia block">Comandas Entregadas</span>
            <div className="font-titulo text-3xl font-extrabold text-tinta mt-1">
              {resumen.totalOrdenes}
            </div>
            <span className="text-[11px] text-salvia font-semibold mt-1 block">
              Ventas cerradas
            </span>
          </div>

          <div className="rounded-3xl border border-salvia/20 bg-white p-5 shadow-sm">
            <span className="text-xs font-bold uppercase text-salvia block">Ticket Promedio</span>
            <div className="font-titulo text-3xl font-extrabold text-tinta mt-1">
              ${promedioPorComanda.toFixed(2)}
            </div>
            <span className="text-[11px] text-salvia font-semibold mt-1 block">
              Por orden procesada
            </span>
          </div>
        </div>
      )}

      {/* Métodos de Pago + Rendimiento de Cocina */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-12 mb-8">
        {/* Desglose por Método de Pago */}
        {resumen && (
          <div className="lg:col-span-7 rounded-3xl border border-salvia/20 bg-white p-6 shadow-sm">
            <h2 className="font-titulo text-lg font-bold text-tinta mb-4 border-b border-salvia/15 pb-2">
              Desglose por Método de Pago
            </h2>

            <div className="space-y-4">
              <div>
                <div className="flex justify-between text-xs font-bold mb-1">
                  <span>💵 Efectivo en Cajón</span>
                  <span className="text-nopal-oscuro">${resumen.desglosePagos.efectivo.toFixed(2)}</span>
                </div>
                <div className="h-3 w-full rounded-full bg-cal overflow-hidden">
                  <div
                    className="h-full bg-nopal transition-all"
                    style={{
                      width: `${
                        resumen.totalVentas > 0
                          ? (resumen.desglosePagos.efectivo / resumen.totalVentas) * 100
                          : 0
                      }%`,
                    }}
                  />
                </div>
              </div>

              <div>
                <div className="flex justify-between text-xs font-bold mb-1">
                  <span>💳 Terminal Bancaria</span>
                  <span className="text-miel-oscuro">${resumen.desglosePagos.terminal.toFixed(2)}</span>
                </div>
                <div className="h-3 w-full rounded-full bg-cal overflow-hidden">
                  <div
                    className="h-full bg-miel transition-all"
                    style={{
                      width: `${
                        resumen.totalVentas > 0
                          ? (resumen.desglosePagos.terminal / resumen.totalVentas) * 100
                          : 0
                      }%`,
                    }}
                  />
                </div>
              </div>

              <div>
                <div className="flex justify-between text-xs font-bold mb-1">
                  <span>📱 Transferencias SPEI</span>
                  <span className="text-tinta">${resumen.desglosePagos.transferencia.toFixed(2)}</span>
                </div>
                <div className="h-3 w-full rounded-full bg-cal overflow-hidden">
                  <div
                    className="h-full bg-salvia transition-all"
                    style={{
                      width: `${
                        resumen.totalVentas > 0
                          ? (resumen.desglosePagos.transferencia / resumen.totalVentas) * 100
                          : 0
                      }%`,
                    }}
                  />
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Rendimiento de Cocina */}
        {cocina && (
          <div className="lg:col-span-5 rounded-3xl border border-salvia/20 bg-white p-6 shadow-sm flex flex-col justify-between">
            <div>
              <h2 className="font-titulo text-lg font-bold text-tinta mb-4 border-b border-salvia/15 pb-2">
                Rendimiento de Cocina (KDS)
              </h2>

              <div className="text-center py-4">
                <span className="text-xs font-bold uppercase text-salvia">Tiempo Promedio de Prep:</span>
                <div className="font-titulo text-4xl font-extrabold text-tinta mt-1">
                  {cocina.promedioMinutos} min
                </div>
                <span
                  className={`mt-2 inline-block rounded-full px-3 py-1 text-xs font-bold ${
                    cocina.promedioMinutos <= cocina.metaMinutos
                      ? 'bg-nopal-claro text-nopal-oscuro'
                      : 'bg-barro-claro text-barro-oscuro'
                  }`}
                >
                  Meta: &le; {cocina.metaMinutos} min
                </span>
              </div>
            </div>

            <p className="text-center text-xs text-salvia border-t border-salvia/10 pt-3">
              Órdenes procesadas en cocina: <strong>{cocina.ordenesProcesadas}</strong>
            </p>
          </div>
        )}
      </div>

      {/* Top 10 Productos Más Vendidos + Comparativo de Sucursales */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-12">
        {/* Top 10 Productos */}
        <div className="lg:col-span-8 rounded-3xl border border-salvia/20 bg-white p-6 shadow-sm">
          <h2 className="font-titulo text-lg font-bold text-tinta mb-4 border-b border-salvia/15 pb-2">
            Top 10 Productos Más Vendidos
          </h2>

          {topProductos.length === 0 ? (
            <p className="py-8 text-center text-salvia">No hay suficientes ventas registradas.</p>
          ) : (
            <div className="space-y-2">
              {topProductos.map((p, i) => (
                <div
                  key={i}
                  className="flex items-center justify-between rounded-xl border border-salvia/15 bg-cal/30 p-3 text-xs"
                >
                  <div className="flex items-center gap-3">
                    <span className="font-titulo text-sm font-extrabold text-salvia w-5">#{i + 1}</span>
                    <strong className="text-tinta">{p.productoNombre}</strong>
                  </div>

                  <div className="flex items-center gap-6">
                    <span className="text-salvia">{p.unidadesVendidas} unidades</span>
                    <strong className="text-nopal-oscuro font-titulo text-sm">
                      ${p.ingresoTotal.toFixed(2)}
                    </strong>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Comparativo Sucursales */}
        <div className="lg:col-span-4 rounded-3xl border border-salvia/20 bg-white p-6 shadow-sm">
          <h2 className="font-titulo text-lg font-bold text-tinta mb-4 border-b border-salvia/15 pb-2">
            Sucursales
          </h2>

          <div className="space-y-3">
            {sucursales.map((s) => (
              <div key={s.id} className="rounded-2xl border border-salvia/15 bg-cal/40 p-4">
                <span className="text-xs font-bold uppercase text-salvia block capitalize">{s.tipo}</span>
                <h3 className="font-titulo text-base font-bold text-tinta">{s.nombre}</h3>
                <div className="mt-2 flex justify-between items-center">
                  <span className="text-xs text-salvia">{s.totalOrdenes} ventas</span>
                  <span className="font-titulo text-base font-extrabold text-nopal-oscuro">
                    ${s.totalVentas.toFixed(2)}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
